import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate, { defaultFormatDate } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, TaxApi } from "../../../../../../../../../../services/Api/Organization/types";
import { useHasInvoicePermission } from "../../../../hooks/useHasInvoicePermission";
import { exportEInvoice } from "../../services/exportInvoice";
import InvoicePdfGeneratorModal from "./InvoicePdfGeneratorModal";
import InvoicePreviewBasicInfoCard from "./InvoicePreviewBasicInfoCard";
import InvoicePreviewItemsCard from "./InvoicePreviewItemsCard";
import InvoicePreviewPersonCard from "./InvoicePreviewPersonCard";
import InvoicePreviewOrdersCard from "./Orders/InvoicePreviewOrdersCard";
import InvoicePreviewPaymentsCard from "./Payments/InvoicePreviewPaymentsCard";

interface Props {
	invoice: InvoiceApi;
	customFieldsConfig: CustomFieldTemplateApi[];
	setInvoice: (invoice: InvoiceApi) => void;
	fetchInvoice: () => Promise<void>;
	taxes: TaxApi[];
}

const InvoicePreview = ({ invoice, customFieldsConfig, setInvoice, fetchInvoice, taxes }: Props) => {
	const isMobile = useWindowSize().isMobile;
	const { addSuccessFlash, addFlash } = useFlash();
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const history = useHistory();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { hasInvoiceManagePermission, hasInvoiceClosePermission, hasPermissionsToCreateNewInvoice } =
		useHasInvoicePermission();
	const [showGeneratorModal, setShowGeneratorModal] = useState(false);

	const handleChangeStatus = async (toStatus: "close" | "remove" | "cancel") => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message:
					toStatus === "remove" ? t("lib:confirmation.message.remove") : t("lib:confirmation.message.action"),
			});
			const params = { include: "all" };
			let res: InvoiceApi;
			switch (toStatus) {
				case "close":
					res = await apiOrganization.closeInvoice(invoice.id, params);
					handleChangeTabTitle(`${getTabTitle(res)} | ${t("modules.invoice.header.title")}`);
					setInvoice(res);
					break;
				case "cancel":
					res = await apiOrganization.cancelInvoice(invoice.id, params);
					handleChangeTabTitle(`${getTabTitle(res)} | ${t("modules.invoice.header.title")}`);
					setInvoice(res);
					break;
				case "remove":
					await apiOrganization.removeInvoice(invoice.id);
					break;
			}
			if (toStatus === "remove") {
				addSuccessFlash(t("lib:common.flash.removed"));
				history.push(`/${organization.id}/documents/invoices`);
			} else {
				addSuccessFlash(t("lib:common.flash.completed"));
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const getTabTitle = (invoice: InvoiceApi) => {
		return invoice.status === "DRAFT"
			? `${t("enums.invoices.document_type.PROFORMA")} #${invoice.id}`
			: `${t("enums.invoices.document_type.INVOICE")} #${invoice.document_number.full_number}`;
	};

	const handleCopy = () => {
		history.push({
			pathname: `/${organization.id}/documents/invoices/new`,
			state: {
				copiedInvoiceId: invoice.id,
			},
		});
	};

	const handleUpdateBasedOnOrders = async () => {
		try {
			const res = await apiOrganization.updateInvoiceBasedOnOrders(invoice.id, { include: "all" });
			setInvoice(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleGeneratePdf = () => {
		setShowGeneratorModal(true);
	};

	const renderTitle = () => (
		<>
			{invoice.status === "DRAFT"
				? `${t("enums.invoices.document_type.PROFORMA")} #${invoice.id}`
				: `${t("enums.invoices.document_type.INVOICE")} #${invoice.document_number.full_number}`}
			<AppFormatResourceStatusLabel
				status={invoice.status}
				customStatuses={[{ status: "CLOSED", label: "success" }]}
			/>
		</>
	);

	const renderSubtitle = () => (
		<ul>
			<li>{FormatDate(invoice.dates?.issue_date, undefined, false, defaultFormatDate)}</li>
			<li>{FormatMoney(invoice?.summary?.price_sum_gross)}</li>
		</ul>
	);

	const avatar = {
		color: "#ff9900",
		date: invoice.dates?.issue_date,
	};

	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.edit"),
			path: `/${organization.id}/documents/invoices/${invoice.id}/edit`,
			hide: invoice.status === "DRAFT" ? !hasPermissionsToCreateNewInvoice : !hasInvoiceManagePermission,
		},
		{
			title: t("modules.invoice.action.download_or_print_pdf.title"),
			action: handleGeneratePdf,
		},
		{
			title: t("lib:common.action.confirm"),
			hide: invoice.status !== "DRAFT" || (!hasInvoiceClosePermission && !hasInvoiceManagePermission),
			action: () => handleChangeStatus("close"),
		},
		{
			title: t("lib:common.action.cancel"),
			hide: invoice.status !== "CLOSED" || !hasInvoiceManagePermission,
			action: () => handleChangeStatus("cancel"),
		},
		{
			title: t("lib:common.action.remove"),
			action: () => handleChangeStatus("remove"),
			hide: !hasInvoiceManagePermission,
		},
		{
			title: t("lib:common.action.copy"),
			action: handleCopy,
		},
		{
			title: t("modules.invoice.field.e-invoice.title"),
			action: () => exportEInvoice(invoice, t, addFlash),
		},
		{
			title: t("modules.invoice.action.update_based_on_orders.title"),
			action: handleUpdateBasedOnOrders,
		},
	];
	const buttons: ButtonProps[] = [
		{
			title: t("modules.invoice.action.download_or_print_pdf.title"),
			action: handleGeneratePdf,
			variant: "primary",
		},
		{
			title: t("lib:common.action.edit"),
			variant: "primary",
			path: `/${organization.id}/documents/invoices/${invoice.id}/edit`,
			hide: invoice.status === "DRAFT" ? !hasPermissionsToCreateNewInvoice : !hasInvoiceManagePermission,
		},
		{
			title: t("lib:common.action.confirm"),
			hide: invoice.status !== "DRAFT" || (!hasInvoiceClosePermission && !hasInvoiceManagePermission),
			variant: "light",
			action: () => handleChangeStatus("close"),
		},
		{
			title: t("lib:common.action.cancel"),
			hide: invoice.status !== "CLOSED" || !hasInvoiceManagePermission,
			variant: "light",
			action: () => handleChangeStatus("cancel"),
		},
		{
			title: t("lib:common.action.remove"),
			dropdown: true,
			action: () => handleChangeStatus("remove"),
			hide: !hasInvoiceManagePermission,
		},
		{
			title: t("lib:common.action.copy"),
			action: handleCopy,
			dropdown: true,
		},
		{
			title: t("modules.invoice.field.e-invoice.title"),
			action: () => exportEInvoice(invoice, t, addFlash),
			dropdown: true,
		},
		{
			title: t("modules.invoice.action.update_based_on_orders.title"),
			dropdown: true,
			action: handleUpdateBasedOnOrders,
		},
	];

	return (
		<div className="content-preview">
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				buttons={isMobile ? [] : buttons}
				avatar={avatar}
				subtitle={renderSubtitle()}
			/>
			<InvoicePreviewBasicInfoCard invoice={invoice} customFieldsConfig={customFieldsConfig} />
			<div className="row">
				{invoice.recipient && (
					<div className={`${invoice.receiver ? "col-md-6" : "col-12"}`}>
						<InvoicePreviewPersonCard
							person={invoice?.recipient}
							title={t("modules.invoice.field.recipient.title")}
						/>
					</div>
				)}
				{invoice.receiver && (
					<div className="col-md-6">
						<InvoicePreviewPersonCard
							person={invoice.receiver}
							title={t("modules.invoice.field.receiver.title")}
						/>
					</div>
				)}
			</div>
			<InvoicePreviewOrdersCard invoice={invoice} setInvoice={setInvoice} />
			<InvoicePreviewItemsCard invoice={invoice} taxes={taxes} />
			<InvoicePreviewPaymentsCard invoice={invoice} fetchInvoice={fetchInvoice} />
			{showGeneratorModal && (
				<InvoicePdfGeneratorModal
					show={showGeneratorModal}
					onHide={() => setShowGeneratorModal(false)}
					customFields={customFieldsConfig}
					invoice={invoice}
					taxes={taxes}
				/>
			)}
		</div>
	);
};

export default InvoicePreview;
